import { useEffect, useState } from "react";

import {
  Button,
  
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  Stack,
  Typography,
  buildPath,
  buildTemplatePath,
  pn,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  StudentGroupFilter as StudentGroupFilterModel,
  StudentGroupListDto,
  StudentGroupListFilter,
  StudentGroupListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { studentGroupPageRoute } from "../StudentGroupPage/StudentGroupPage";
import { StudentGroupFilter } from "./StudentGroupFilter";
import { StudentGroupTable } from "./StudentGroupTable";

const routeName = "studentGroups";

export const studentGroupListPageRoute: PageRoute = {
  handle: "Groups",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StudentGroupListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentGroup;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentGroupListFilter>({
    sortObjects: [{ property: pn<StudentGroupListDto>("name"), isDesc: false }],
    groupFilter: {
      inactive: false,
    },
  });
  const [initListFilter] = useState<StudentGroupListFilter>(listFilter);
  const [listItems, setListItems] = useState<StudentGroupListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentGroupListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentGroups.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentGroups.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentGroups.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentGroupListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StudentGroupFilter
        filter={listFilter.groupFilter || ({} as StudentGroupFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.groupFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.studentGroups.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{studentGroupListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate({
                      pathname: studentGroupPageRoute.getPath({ id: 0, crudMode: "edit" }),
                    })
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StudentGroupTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentGroups={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
